import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Button,
  Modal,
  Form,
  Alert,
  Card,
  Badge,
  ListGroup,
  Spinner,
} from "react-bootstrap";
import PageHeader from "../../components/common/PageHeader";
import ContactSearch from "../../components/contacts/ContactSearch";
import ContactTable from "../../components/contacts/ContactTable";
import {
  fetchCategories,
  fetchContactsByCategory,
  createContact,
  deleteContacts,
  assignCategoriesToContact,
} from "../../service/apiService";

export default function ContactsPage() {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIds, setDeleteIds] = useState([]);
  const [contactCategoryId, setContactCategoryId] = useState("");
  const [saveFeedback, setSaveFeedback] = useState(null);

  const [newContact, setNewContact] = useState({
    name: "",
    phone: "",
    tag: "",
    is_opted_in: 1,
  });

  const syncSelectedCategory = useCallback((nextCategories) => {
    setSelectedCategory((current) => {
      if (!current) return current;

      const refreshed = nextCategories.find(
        (category) => category.id === current.id
      );

      return refreshed || current;
    });
  }, []);

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const res = await fetchCategories();
      if (res.status === "success") {
        const nextCategories = res.data || [];
        setCategories(nextCategories);
        syncSelectedCategory(nextCategories);
      }
    } finally {
      setLoadingCategories(false);
    }
  }, [syncSelectedCategory]);

  const loadContactsForCategory = useCallback(async (category) => {
    if (!category) {
      setContacts([]);
      return;
    }

    setLoadingContacts(true);
    try {
      const res = await fetchContactsByCategory(category.id);
      if (res.status === "success") {
        setContacts(res.data || []);
      } else {
        setContacts([]);
      }
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (selectedCategory) {
      loadContactsForCategory(selectedCategory);
    }
  }, [selectedCategory, loadContactsForCategory]);

  useEffect(() => {
    if (selectedCategory) {
      setContactCategoryId(String(selectedCategory.id));
    }
  }, [selectedCategory]);

  const handleSelectCategory = (category) => {
    setSelected([]);
    setSearch("");
    setSelectedCategory(category);
    setContactCategoryId(String(category.id));
  };

  const handleAddContact = async () => {
    if (!contactCategoryId) {
      setSaveFeedback({
        variant: "warning",
        message: "Please select a category first.",
      });
      return;
    }

    const payload = {
      ...newContact,
      name: newContact.name.trim(),
      phone: newContact.phone.trim(),
      tag: newContact.tag.trim(),
    };

    try {
      const res = await createContact(payload);

      if (res.status !== "success") {
        setSaveFeedback({
          variant: "danger",
          message: res.message || "Failed to create contact.",
        });
        return;
      }

      const createdContactId =
        res?.data?.id ??
        res?.data?.contact?.id ??
        res?.contact?.id ??
        res?.id ??
        null;

      if (!createdContactId) {
        setSaveFeedback({
          variant: "warning",
          message:
            "Contact was saved, but the server response did not include the new ID.",
        });
      } else {
        const categoryLinkRes = await assignCategoriesToContact(
          createdContactId,
          [Number(contactCategoryId)]
        );

        if (categoryLinkRes.status !== "success") {
          setSaveFeedback({
            variant: "danger",
            message:
              categoryLinkRes.message ||
              "Contact created, but category linking failed.",
          });
          return;
        }
      }

      const selectedCategoryObject =
        categories.find(
          (category) => category.id === Number(contactCategoryId)
        ) || null;

      if (selectedCategoryObject) {
        setSelectedCategory(selectedCategoryObject);
        setContactCategoryId(String(selectedCategoryObject.id));
      }

      setNewContact({ name: "", phone: "", tag: "", is_opted_in: 1 });
      setSaveFeedback({
        variant: "success",
        message: "Contact saved successfully.",
      });

      if (selectedCategoryObject) {
        await loadContactsForCategory(selectedCategoryObject);
      }
      await loadCategories();
    } catch (error) {
      setSaveFeedback({
        variant: "danger",
        message: error?.message || "Server error while saving contact.",
      });
    }
  };

  const handleDeleteSingle = (id) => {
    setDeleteIds([id]);
    setShowDeleteModal(true);
  };

  const handleDeleteSelected = () => {
    if (!selected.length) return;
    setDeleteIds(selected);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    const res = await deleteContacts(deleteIds);

    if (res.status === "success") {
      setSelected([]);
      if (selectedCategory) {
        await loadContactsForCategory(selectedCategory);
      }
      await loadCategories();
    } else {
      alert(res.message || "Delete failed");
    }

    setShowDeleteModal(false);
    setDeleteIds([]);
  };

  const toggleSelect = (id) => {
    setSelected((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id]
    );
  };

  const filteredContacts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return contacts;

    return contacts.filter((contact) => {
      return (
        (contact.name || "").toLowerCase().includes(term) ||
        (contact.phone || "").toLowerCase().includes(term) ||
        (contact.tag || "").toLowerCase().includes(term)
      );
    });
  }, [contacts, search]);

  const categoryCount = categories.length;
  const selectedCategoryCount = selectedCategory?.contact_count ?? 0;

  return (
    <Container fluid className="admin-page contacts-page">
      <PageHeader
        title="Contacts"
        subtitle="Choose a category first, then view the contacts inside it"
      />

      <div className="contacts-workspace">
      <div className="contacts-page-stats">
        <div className="contacts-page-stats-row">
          <div className="contacts-page-stats-left">
          <Badge bg="light" text="dark" className="me-2 px-3 py-2 shadow-sm">
            Categories: {categoryCount}
          </Badge>
          <Badge bg="light" text="dark" className="px-3 py-2 shadow-sm">
            Contacts in view: {selectedCategoryCount}
          </Badge>
          </div>

          <div className="contacts-page-stats-right">
          <Button
            onClick={() => {
              if (selectedCategory) {
                setContactCategoryId(String(selectedCategory.id));
              }
              setShowModal(true);
            }}
            className="px-4 shadow-sm"
            disabled={!selectedCategory}
            style={{
              background: "linear-gradient(45deg, #4e73df, #224abe)",
              border: "none",
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Add Contact
          </Button>
          </div>
        </div>
      </div>

      <div className="contacts-page-grid">
        <div className="contacts-column contacts-column-categories">
          <Card className="shadow-sm border-0 contacts-panel">
            <Card.Header className="fw-bold d-flex justify-content-between align-items-center">
              <span>Categories</span>
              {loadingCategories && <Spinner animation="border" size="sm" />}
            </Card.Header>
            <div className="categories-scroll-area">
              <ListGroup variant="flush">
                {categories.map((category) => (
                  <ListGroup.Item
                    key={category.id}
                    action
                    active={selectedCategory?.id === category.id}
                    onClick={() => handleSelectCategory(category)}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div className="me-3">
                      <div className="fw-semibold">{category.name}</div>
                      <small className="text-muted">Tap to view contacts</small>
                    </div>
                    <Badge
                      bg={selectedCategory?.id === category.id ? "light" : "secondary"}
                      text={selectedCategory?.id === category.id ? "dark" : "light"}
                    >
                      {category.contact_count ?? 0}
                    </Badge>
                  </ListGroup.Item>
                ))}

                {!loadingCategories && categories.length === 0 && (
                  <ListGroup.Item className="text-muted">
                    No categories found
                  </ListGroup.Item>
                )}
              </ListGroup>
            </div>
          </Card>
        </div>

        <div className="contacts-column contacts-column-contacts">
          <Card className="shadow-sm border-0 contacts-panel">
            <Card.Header className="fw-bold d-flex justify-content-between align-items-center">
              <span>
                {selectedCategory
                  ? `Contacts in ${selectedCategory.name}`
                  : "Select a category"}
              </span>
              {loadingContacts && <Spinner animation="border" size="sm" />}
            </Card.Header>

            <Card.Body className="contacts-body">
              {!selectedCategory && (
                <div className="text-muted">
                  Pick a category from the left to load its contacts.
                </div>
              )}

              {selectedCategory && (
                <>
                  <div className="contacts-controls">
                    <ContactSearch
                      value={search}
                      onChange={setSearch}
                      placeholder="Search within this category..."
                    />

                    {selected.length > 0 && (
                      <Card className="mt-3 mb-0 border-0 bg-light">
                        <Card.Body className="d-flex justify-content-between align-items-center">
                          <div>
                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                            {selected.length} selected
                          </div>
                          <Button variant="danger" onClick={handleDeleteSelected}>
                            <i className="bi bi-trash-fill me-2"></i>
                            Delete Selected
                          </Button>
                        </Card.Body>
                      </Card>
                    )}
                  </div>

                  <div className="contacts-scroll-area">
                    {loadingContacts && <Spinner animation="border" />}

                    {!loadingContacts && filteredContacts.length === 0 && (
                      <div className="contacts-empty text-muted">
                        {search.trim()
                          ? "No contacts match your search."
                          : "No contacts found in this category."}
                      </div>
                    )}

                    {!loadingContacts && filteredContacts.length > 0 && (
                      <ContactTable
                        contacts={filteredContacts}
                        selected={selected}
                        onToggle={toggleSelect}
                        onDelete={handleDeleteSingle}
                      />
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
      </div>

      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSaveFeedback(null);
        }}
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            <i className="bi bi-person-plus-fill me-2 text-primary"></i>
            Add New Contact
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {saveFeedback && (
            <Alert
              variant={saveFeedback.variant}
              dismissible
              onClose={() => setSaveFeedback(null)}
            >
              {saveFeedback.message}
            </Alert>
          )}

          <div className="mb-3">
            <div className="text-muted small mb-1">Selected category</div>
            <Badge bg="primary">
              {categories.find((category) => category.id === Number(contactCategoryId))
                ?.name || selectedCategory?.name}
            </Badge>
          </div>

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={contactCategoryId}
                onChange={(e) => setContactCategoryId(e.target.value)}
              >
                <option value="">-- Select Category --</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                placeholder="Enter contact name"
                value={newContact.name}
                onChange={(e) =>
                  setNewContact({ ...newContact, name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                placeholder="Enter phone number"
                value={newContact.phone}
                onChange={(e) =>
                  setNewContact({ ...newContact, phone: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tag</Form.Label>
              <Form.Control
                placeholder="VIP / Retail / Wholesale"
                value={newContact.tag}
                onChange={(e) =>
                  setNewContact({ ...newContact, tag: e.target.value })
                }
              />
            </Form.Group>

            <Form.Check
              type="switch"
              label="Opted In for WhatsApp"
              checked={newContact.is_opted_in === 1}
              onChange={(e) =>
                setNewContact({
                  ...newContact,
                  is_opted_in: e.target.checked ? 1 : 0,
                })
              }
            />
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddContact}
            style={{
              background: "linear-gradient(45deg, #1cc88a, #13855c)",
              border: "none",
            }}
          >
            <i className="bi bi-save-fill me-2"></i>
            Save Contact
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Body className="text-center p-4">
          <div
            className="mx-auto mb-3 d-flex align-items-center justify-content-center"
            style={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              backgroundColor: "#fde2e2",
            }}
          >
            <i
              className="bi bi-exclamation-triangle-fill"
              style={{ fontSize: 32, color: "#dc3545" }}
            ></i>
          </div>

          <h5 className="fw-bold mb-2">Confirm Deletion</h5>

          <p className="text-muted mb-4">
            {deleteIds.length === 1
              ? "Are you sure you want to delete this contact?"
              : `Are you sure you want to delete ${deleteIds.length} contacts?`}
            <br />
            This action cannot be undone.
          </p>

          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="light"
              onClick={() => setShowDeleteModal(false)}
              className="px-4"
            >
              Cancel
            </Button>

            <Button
              onClick={confirmDelete}
              className="px-4"
              style={{
                background: "linear-gradient(45deg, #dc3545, #b02a37)",
                border: "none",
              }}
            >
              <i className="bi bi-trash-fill me-2"></i>
              Delete
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
}
